import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Head, Loader, EmptyBox, timeAgo } from "./shared";
import { Bot, X, CheckCircle2, MessageSquare, MessagesSquare, DollarSign, UserPlus, Hash } from "lucide-react";

const INTENT_LABEL = {
  website_project: "Ready for project",
  needs_proposal: "Wants a proposal",
  wants_content: "Wants content",
  wants_full_website: "Wants full website",
  general: "General question",
};

export function AssistantConversations() {
  const [data, setData] = useState(null);
  const [active, setActive] = useState(null);
  const [transcript, setTranscript] = useState(null);
  const [loadingT, setLoadingT] = useState(false);
  const [usage, setUsage] = useState(null);

  useEffect(() => {
    api.get("/admin/assistant-sessions").then((r) => setData(r.data)).catch(() => setData({ sessions: [], total: 0 }));
    api.get("/admin/assistant/usage").then((r) => setUsage(r.data)).catch(() => {});
  }, []);

  const open = async (s) => {
    setActive(s);
    setTranscript(null);
    setLoadingT(true);
    try {
      const r = await api.get(`/admin/assistant-sessions/${s.id}`);
      setTranscript(r.data.messages || []);
    } catch (e) {
      setTranscript([]);
    } finally {
      setLoadingT(false);
    }
  };

  if (!data) return <Loader />;
  const sessions = data.sessions || [];

  return (
    <div data-testid="assistant-conversations">
      <Head title="Assistant Conversations" desc="Public AI Assistant chats from your website — intent, readiness and full transcripts." />

      {usage && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6" data-testid="concierge-usage">
          {[
            { icon: MessagesSquare, label: `Conversations · ${usage.month}`, value: usage.conversations_this_month, sub: `${usage.conversations_total} all-time` },
            { icon: MessageSquare, label: "Messages this month", value: usage.messages_total, sub: `${usage.messages_user} in · ${usage.messages_assistant} out` },
            { icon: Hash, label: "Est. tokens", value: (usage.est_tokens || 0).toLocaleString(), sub: "~4 chars/token" },
            { icon: DollarSign, label: "Est. cost this month", value: `$${(usage.est_cost_usd || 0).toFixed(2)}`, sub: `@ $${usage.cost_basis_per_1m}/1M tok` },
            { icon: UserPlus, label: "Leads captured", value: usage.leads_captured, sub: "via chat" },
          ].map((c) => {
            const Ic = c.icon;
            return (
              <div key={c.label} className="bg-navy-800 border border-white/10 p-4">
                <div className="flex items-center gap-2 text-gray-400 text-[11px] uppercase tracking-wide mb-2"><Ic size={13} className="text-gold" /> {c.label}</div>
                <div className="text-2xl font-heading text-white leading-none">{c.value}</div>
                <div className="text-[11px] text-gray-500 mt-1">{c.sub}</div>
              </div>
            );
          })}
        </div>
      )}
      {usage && (
        <p className="text-[11px] text-gray-500 -mt-3 mb-6">
          Cost is a rough estimate for pricing guidance (based on message length × a blended token price), not your exact provider bill.
        </p>
      )}

      {sessions.length === 0 ? (
        <EmptyBox text="No public assistant conversations yet." />
      ) : (
        <div className="space-y-3">
          {sessions.map((s) => (
            <button key={s.id} onClick={() => open(s)} data-testid={`conversation-row-${s.id}`}
              className="w-full text-left bg-navy-800 border border-white/10 hover:border-gold/40 transition-colors p-4 flex items-start gap-4">
              <div className="w-10 h-10 rounded bg-sky-500/15 text-sky-400 flex items-center justify-center shrink-0">
                <Bot size={18} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  {s.ready && <span className="inline-flex items-center gap-1 text-xs text-emerald-300 bg-emerald-500/15 px-2 py-0.5"><CheckCircle2 size={12} /> Qualified</span>}
                  {s.intent && <span className="text-xs text-gold bg-gold/10 px-2 py-0.5">{INTENT_LABEL[s.intent] || s.intent}</span>}
                  <span className="text-xs text-gray-500 inline-flex items-center gap-1"><MessageSquare size={12} /> {s.message_count} msgs</span>
                </div>
                <p className="text-gray-200 mt-1.5 truncate">{s.snippet || "—"}</p>
                <p className="text-xs text-gray-500 mt-1">{timeAgo(s.updated_at || s.created_at)}</p>
              </div>
            </button>
          ))}
        </div>
      )}

      {active && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={() => setActive(null)} data-testid="conversation-modal">
          <div className="bg-navy border border-white/10 w-full max-w-2xl max-h-[85vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between p-4 border-b border-white/10">
              <div className="flex items-center gap-2">
                <Bot size={18} className="text-sky-400" />
                <span className="text-white font-medium">Conversation</span>
                {active.intent && <span className="text-xs text-gold bg-gold/10 px-2 py-0.5">{INTENT_LABEL[active.intent] || active.intent}</span>}
              </div>
              <button onClick={() => setActive(null)} data-testid="conversation-close" className="text-gray-400 hover:text-white"><X size={18} /></button>
            </div>
            <div className="p-4 overflow-y-auto space-y-3">
              {loadingT ? <Loader /> : (transcript && transcript.length ? transcript.map((m, i) => (
                <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                  <div className={`max-w-[80%] px-3 py-2 text-sm ${m.role === "user" ? "bg-gold/15 text-gray-100" : "bg-navy-800 text-gray-200 border border-white/10"}`}>
                    {m.content}
                  </div>
                </div>
              )) : <p className="text-gray-500 text-center py-8">No messages in this conversation.</p>)}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
